import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import './Login.css';
import logo from '../../assets/logo/ErisConnectLogo.png';

export default function Login(props) {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.login(username, password);
      const { access_token, user } = result.data;

      localStorage.setItem('ejt_token', access_token);
      if (user) localStorage.setItem('ejt_user', JSON.stringify(user));
      await props.onLoginSuccess();          // ← add this line
      // ── Redirect (same as before) ─────────────────────────────
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      // Show the backend message (e.g. "Invalid credentials") directly.
      // Fallback to a generic message only if nothing came from server.
      const msg =
        err?.response?.data?.message ||   // axios raw error
        err?.apiData?.message        ||   // unwrapped api.js error
        err?.message                 ||   // Error object
        'Login failed. Please check your credentials and try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

    return (
        <div className="login-page">
            <div className="login-card">

                {/* ── Left Panel ── */}
                <div className="login-left">
                    <div className="login-brand">
                        <div className="login-brand-logo-wrap">
                            <img
                                src={logo}
                                alt="Eris Connect"
                                className="login-brand-logo"
                            />
                        </div>
                    </div>
                    <div className="login-hero">
                        <h1 className="login-hero-title">Employee Journey Tracker</h1>
                        <p className="login-hero-sub">
                            Track every milestone across the 180-day onboarding journey —
                            from Day 1 to Day 180  integration.
                        </p>
                    </div>
                    <div className="login-stats">
                        <div className="login-stat">
                            <div className="login-stat-num">180</div>
                            <div className="login-stat-label">Day Journey</div>
                        </div>
                        <div className="login-stat-divider" />
                        <div className="login-stat">
                            <div className="login-stat-num">7</div>
                            <div className="login-stat-label">Touchpoints</div>
                        </div>
                        <div className="login-stat-divider" />
                        <div className="login-stat">
                            <div className="login-stat-num">100%</div>
                            <div className="login-stat-label">Visibility</div>
                        </div>
                    </div>
                </div>

                {/* ── Right Panel ── */}
                <div className="login-right">
                    <div className="login-form-header">
                        <h2 className="login-form-title">Welcome back</h2>
                        <p className="login-form-sub">Sign in to your HR portal</p>
                    </div>

                    {error && (
                        <div className="login-alert login-alert-error">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="login-alert login-alert-success">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Login successful! Redirecting...
                        </div>
                    )}

                    <form onSubmit={handleLogin} noValidate>
                        <div className="login-field">
                            <label className="login-label" htmlFor="username">Username</label>
                            <div className="login-input-wrap">
                                <svg className="login-input-icon" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.3" />
                                    <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                </svg>
                                <input
                                    id="username"
                                    className="login-input"
                                    type="text"
                                    placeholder="Enter your username"
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    autoComplete="username"
                                />
                            </div>
                        </div>

                        <div className="login-field">
                            <label className="login-label" htmlFor="password">Password</label>
                            <div className="login-input-wrap">
                                <svg className="login-input-icon" viewBox="0 0 16 16" fill="none">
                                    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                                    <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                </svg>
                                <input
                                    id="password"
                                    className="login-input login-input-pwd"
                                    type={showPwd ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="login-toggle"
                                    onClick={() => setShowPwd(p => !p)}
                                    tabIndex={-1}
                                >
                                    {showPwd ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className={`login-btn ${loading ? 'loading' : ''}`}
                            disabled={loading || success}
                        >
                            {loading
                                ? <span className="login-spinner" />
                                : 'Sign in'
                            }
                        </button>
                    </form>

                    <div className="login-divider">
                        <span className="login-divider-line" />
                        <span className="login-divider-text">secured by JWT</span>
                        <span className="login-divider-line" />
                    </div>

                    <p className="login-footer">
                        Forgot your password?{' '}
                        <span className="login-footer-link">Contact your system administrator</span>
                    </p>
                </div>

            </div>
        </div>
    );
}